import * as Yup from "yup";
import { useState } from "react";
import { useFormik } from "formik";
import { useMutation, useQuery } from "@apollo/client";

import { Button, getInput } from "../../../../components/ui";
import { BATCH_MOVE_WAREHOUSE_ITEMS } from "../../../../graphql/mutation/WarehouseItem";
import { FETCH_WAREHOUSE_LOCATIONS } from "../../../../graphql/query/WarehouseLocation";
import { FETCH_WAREHOUSE_SITES } from "../../../../graphql/query/WarehouseSite";
import {
  buildBatchMoveWarning,
  needsBatchMoveConfirmation,
} from "./batchMoveConfirmation";

const BatchMoveLocationBody = props => {
  const {
    closeModal,
    fromLocationNames = [],
    itemCount = 0,
    onSuccess,
    selection,
    siteId,
  } = props || {};
  const [isConfirming, setIsConfirming] = useState(false);
  const [batchMoveWarehouseItems, { loading, data, error, reset }] =
    useMutation(BATCH_MOVE_WAREHOUSE_ITEMS, {
      refetchQueries: [
        "FetchWarehouseItemsSearchResults",
        "FetchListItems",
        "FetchWarehouseMoveV2",
        "FetchWarehouseItem",
      ],
    });

  const { data: { warehouseLocations = [] } = {} } = useQuery(
    FETCH_WAREHOUSE_LOCATIONS,
  );
  const { data: { warehouseSites = [] } = {} } = useQuery(
    FETCH_WAREHOUSE_SITES,
  );

  const handleCancel = () => {
    formik?.resetForm();
    reset();
    setIsConfirming(false);
    closeModal?.();
  };

  const moveItems = async ({ locationId, locref }) => {
    const response = await batchMoveWarehouseItems({
      variables: { input: { ...selection, locationId, locref } },
    });
    const { success } = response?.data?.batchMoveWarehouseItems || {};
    if (success) {
      formik.resetForm();
      onSuccess?.();
      closeModal?.();
    }
  };

  const formik = useFormik({
    initialValues: { siteId, locationId: null, locref: "" },
    enableReinitialize: true,
    validationSchema: Yup.object({
      locationId: Yup.number()
        .typeError("Please select a location")
        .required("Please select a location"),
    }),
    onSubmit: async values => {
      if (needsBatchMoveConfirmation(itemCount) && !isConfirming) {
        setIsConfirming(true);
        return;
      }
      await moveItems(values);
    },
  });

  const siteLocations = warehouseLocations?.filter(
    location => location?.siteId == siteId,
  );

  const leftInputFields = [
    {
      label: "Site",
      name: "siteId",
      disabled: true,
      type: "multi-select",
      options: warehouseSites?.map(site => ({
        label: site?.name,
        value: site?.id,
      })),
    },
    {
      label: "Location",
      name: "locationId",
      type: "multi-select",
      options: siteLocations?.map(location => ({
        label: location.description,
        value: location.id,
      })),
    },
  ];

  const rightInputFields = [
    { label: "Movement Notes", name: "locref", type: "textarea" },
  ];

  const errorMessage = data?.batchMoveWarehouseItems?.error || error?.message;
  const itemCountLabel = `${itemCount} warehouse item${itemCount === 1 ? "" : "s"}`;
  const toLocationName = siteLocations?.find(
    location => location.id == formik.values.locationId,
  )?.description;

  const renderInputs = fields =>
    fields.map(field => (
      <div key={field.name} className="w-full">
        {getInput({ ...field, formik })}
      </div>
    ));

  if (isConfirming && !errorMessage) {
    return (
      <div className="flex w-full flex-col gap-6 p-6">
        <div className="text-2xl font-bold">Batch Move Location</div>
        <div className="text-base">
          {buildBatchMoveWarning({
            fromLocationNames,
            itemCount,
            toLocationName,
          })}
        </div>
        <div className="flex flex-wrap justify-end gap-4">
          <Button
            action="default"
            label="Cancel"
            disabled={loading}
            onClick={() => setIsConfirming(false)}
          />
          <Button
            action="danger"
            label="I am sure!"
            loading={loading}
            disabled={loading}
            onClick={formik.submitForm}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4 p-6">
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="flex flex-1 flex-col">
          <div className="text-2xl font-bold">Batch Move Location</div>
          <div className="text-sm">{`This will move ${itemCountLabel}`}</div>
        </div>
        <div className="flex flex-wrap gap-4">
          <Button action="default" label="Cancel" onClick={handleCancel} />
          {!errorMessage && (
            <Button
              label="Move Items"
              loading={loading}
              disabled={loading}
              onClick={formik.submitForm}
            />
          )}
        </div>
      </div>
      {errorMessage ? (
        <div>{errorMessage}</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 pb-6 pt-6 lg:grid-cols-2">
          <div className="grid grid-cols-1 gap-4">
            {renderInputs(leftInputFields)}
          </div>
          <div className="grid grid-cols-1 gap-4">
            {renderInputs(rightInputFields)}
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchMoveLocationBody;
